import { Link } from 'react-router-dom';

/**
 * /terms — Jubuddy game (operated by Jubit AI Universe).
 * Adapted from jubit-ai-universe Terms.tsx with game-specific
 * acceptable-use scope (no botting, no API resale, no abusive prompts).
 */
export function TermsPage() {
  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Link to="/" className="mb-4 inline-flex items-center text-sm text-neutral-400 hover:text-neutral-100">
          ← Back to Home
        </Link>
        <h1 className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-3xl font-bold text-transparent sm:text-4xl">
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-neutral-400">Last updated: April 2026</p>

        <div className="mt-8 space-y-6">
          <Section title="1. Acceptance of Terms">
            <p className="mb-3">
              By accessing Jubuddy ("the Service") at jubuddy.com, gghere.com, or any embedded surface on jubit.ai,
              you agree to be bound by these Terms of Service ("Terms"). The Service is operated by{' '}
              <strong>Jubit AI Universe</strong>.
            </p>
            <p>
              If you do not agree, do not use the Service. We may modify these Terms at any time; modifications take
              effect when posted to this page.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p className="mb-3">Jubuddy provides:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>A casual 2D tower-defense game with keyword-fusion towers and 12 mascot heroes.</li>
              <li>A campaign mode and an endless-run mode.</li>
              <li>A Studio for prompt authoring, theme browsing, and AI image generation.</li>
              <li>Integration with Jubit SSO for cross-product sign-in.</li>
            </ul>
            <p className="mt-3">
              The Service is provided "as is" without warranties of availability, accuracy, or fitness for any
              particular purpose.
            </p>
          </Section>

          <Section title="3. User Accounts">
            <p className="mb-3">Sign-in is optional but enables cross-device sync. By creating an account you agree to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Provide accurate registration information.</li>
              <li>Maintain the security of your credentials.</li>
              <li>Accept responsibility for all activity under your account.</li>
              <li>Notify us immediately of unauthorized use.</li>
            </ul>
            <p className="mt-3">
              You must be at least 13 years old. Adult-themed content in the Studio requires an additional 18+
              confirmation.
            </p>
          </Section>

          <Section title="4. Acceptable Use">
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Submit prompts that incite violence, depict minors in sexual contexts, or violate any law.</li>
              <li>Use automated tools to grind, farm rewards, or scrape catalog data.</li>
              <li>Reverse-engineer the simulator, fusion engine, or bridge protocol to gain advantage.</li>
              <li>Resell or redistribute API access obtained via your account.</li>
              <li>Infringe intellectual property rights of others (including character likeness in prompts).</li>
              <li>Attempt to gain unauthorized access to other users' accounts or our systems.</li>
              <li>Use the Service for commercial purposes without a separate written agreement.</li>
            </ul>
            <p className="mt-3">We may suspend or terminate accounts for violations.</p>
          </Section>

          <Section title="5. Intellectual Property">
            <p className="mb-3">
              The Service, including the simulator, fusion-engine, hero kits, map data, sprite art, and audio voices,
              are the exclusive property of Jubit AI Universe and its licensors.
            </p>
            <p className="mb-3">
              You retain ownership of prompts and content you author. By submitting content to the Studio you grant us
              a non-exclusive, worldwide, royalty-free license to process, display, and (where applicable) train balance
              models on aggregated, anonymized data.
            </p>
            <p className="mb-3">
              AI-generated images carry the licensing terms of the provider that generated them (e.g., Pollinations,
              Google Gemini, OpenAI). Review the underlying provider's terms before commercial reuse.
            </p>
            <p>
              Planet terrain is produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and
              Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved. The
              organisations in charge of the Copernicus programme by law or by delegation do not incur any liability
              for any use of the Copernicus WorldDEM-30.
            </p>
          </Section>

          <Section title="6. Privacy">
            See our <Link to="/privacy" className="underline">Privacy Policy</Link>. We comply with GDPR, CCPA, and the
            Hong Kong Personal Data (Privacy) Ordinance (Cap. 486).
          </Section>

          <Section title="7. Third-Party AI Providers">
            <p className="mb-3">
              The Studio's image generation forwards your sanitized prompt to one or more of: Pollinations, Google
              (Gemini), OpenAI, xAI (Grok), Alibaba Cloud (Qwen). Each provider has its own terms governing model
              usage, content moderation, and output rights.
            </p>
            <p>
              We do not warrant the accuracy, safety, or availability of any third-party model. If a provider rejects
              your prompt for safety reasons, we surface that rejection but do not override it.
            </p>
          </Section>

          <Section title="8. Governing Law">
            <p className="mb-3">
              These Terms are governed by the laws of the Hong Kong Special Administrative Region, without regard to
              conflict-of-law provisions.
            </p>
            <p>
              Disputes shall be subject to the exclusive jurisdiction of Hong Kong SAR courts. Local mandatory consumer
              protection laws may also apply for users outside Hong Kong.
            </p>
          </Section>

          <Section title="9. Contact">
            <ul className="list-none space-y-1">
              <li><strong>Legal:</strong> <a className="underline" href="mailto:legal@jubitai.com">legal@jubitai.com</a></li>
              <li><strong>Support:</strong> <a className="underline" href="mailto:support@jubitai.com">support@jubitai.com</a></li>
              <li><strong>Address:</strong> Hong Kong SAR</li>
            </ul>
          </Section>
        </div>
      </div>
    </main>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
      <h2 className="mb-3 text-xl font-semibold sm:text-2xl">{title}</h2>
      <div className="text-sm text-neutral-300 sm:text-base">{children}</div>
    </section>
  );
}

export default TermsPage;
